class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.footer = null;
    this.content = null;
    this.formInView = true;
    this.footerInView = false;
    this.formObserver = null;
    this.footerObserver = null;
  }

  connectedCallback() {
    this.form = this.closest('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"]');
    this.content = this.querySelector('.sticky-add-to-cart__content');

    if (!this.form || !this.content) return;

    // Réserve l'espace du sticky dans le flux pour éviter le changement de
    // hauteur du document quand le contenu passe en position: fixed. Sans cela,
    // le contenu sort du flux → la page raccourcit → le footer remonte dans le
    // viewport → le sticky se désactive → boucle infinie de clignotement.
    requestAnimationFrame(() => {
      this.style.minHeight = `${this.offsetHeight}px`;
      this.#initObservers();
    });
  }

  disconnectedCallback() {
    this.formObserver?.disconnect();
    this.footerObserver?.disconnect();
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  }

  #initObservers() {
    // 16/10/2026 — Bascule sur la place du BOUTON (l'hôte), plus sur tout le
    // formulaire, et marge ASYMÉTRIQUE. Avant : un IntersectionObserver sur le
    // formulaire avec 300 px de marge au-dessus de l'écran ; la barre n'apparaissait
    // qu'une fois le formulaire ENTIER sorti PLUS 300 px de défilement (signalé par
    // Mika : « il faut encore scroller »). Désormais :
    // - en DESCENDANT, la barre apparaît dès que le bouton a quitté le haut de l'écran ;
    // - en REMONTANT, elle rend la main quand le bouton revient à 300 px du haut, la
    //   marge du 10/10 : la fermeture se fait hors champ, sans trou à l'écran.
    // Un bouton encore SOUS l'écran (pas encore atteint) ne déclenche rien.
    this.lastY = window.scrollY;
    this.onScroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.#computeFormInView();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.#computeFormInView();

    if (!this.footer) return;

    this.footerObserver = new IntersectionObserver(([entry]) => {
      this.footerInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.footerObserver.observe(this.footer);
  }

  #computeFormInView() {
    // Le sens ne change qu'après 12 px dans l'autre sens : un tremblement du doigt
    // (ou le rebond de fin de défilement sur iOS) ne fait pas ouvrir et fermer la
    // barre en boucle dans la zone des 300 px.
    const y = window.scrollY;
    if (y < this.lastY - 12) {
      this.goingUp = true;
      this.lastY = y;
    } else if (y > this.lastY + 12) {
      this.goingUp = false;
      this.lastY = y;
    } else if (this.goingUp ? y < this.lastY : y > this.lastY) {
      this.lastY = y;
    }
    const goingUp = this.goingUp === true;

    // Remontée très rapide : si la place du bouton entre à l'écran PENDANT les
    // 300 ms de l'animation de fermeture, on coupe l'animation et on rend la main
    // tout de suite — sinon la place vide passerait à l'écran (le clignotement).
    if (this.dataset.closing === 'true' && this.#slotInView()) {
      clearTimeout(this.closingTimeout);
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
    }
    const bottom = this.getBoundingClientRect().bottom;
    let inView;
    if (bottom > 0) inView = true; // bouton (au moins en partie) visible, ou pas encore atteint
    else if (bottom <= -300) inView = false; // bouton loin au-dessus : barre
    else inView = goingUp; // 0 à 300 px au-dessus : barre en descendant, bouton en remontant
    if (inView === this.formInView) return;
    this.formInView = inView;
    this.#updateDisplay();
  }

  #updateDisplay() {
    const shouldBeActive = !this.formInView && !this.footerInView;
    const isActive = this.dataset.active === 'true';

    if (shouldBeActive === isActive) return;

    clearTimeout(this.closingTimeout);

    if (shouldBeActive) {
      delete this.dataset.closing;
      this.dataset.active = 'true';
      this.content.classList.add('color-scheme-1');
      return;
    }

    this.dataset.active = 'false';

    // Pendant l'animation de fermeture le contenu reste `position: fixed` : sa
    // place dans le flux — c'est-à-dire le vrai bouton d'ajout au panier — est
    // donc VIDE pendant 300 ms. Tant que cette place est hors écran (fermeture
    // causée par le footer), personne ne le voit. Mais quand elle est visible,
    // cas du desktop où la colonne d'infos est `position: sticky` et revient à
    // l'écran dès qu'on remonte, le bouton disparaît sous les yeux du visiteur
    // puis réapparaît. Dans ce cas on rend la main au flux immédiatement : le
    // passage de la barre fixe au bouton en place se fait sans trou.
    if (this.#slotInView()) {
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
      return;
    }

    this.dataset.closing = 'true';

    this.closingTimeout = setTimeout(() => {
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
    }, 300);
  }

  // La place réservée dans le flux (l'hôte, gardé à sa hauteur par min-height)
  // croise-t-elle le viewport ?
  #slotInView() {
    const slot = this.getBoundingClientRect();

    return slot.bottom > 0 && slot.top < window.innerHeight;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
